// client/src/components/Documents/DocumentsTab.js
// Advanced Document Management with Versioning, OCR, and Backups

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext.jsx";
import {
  Upload,
  FileText,
  File,
  Image as ImageIcon,
  Download,
  Eye,
  Trash2,
  Search,
  Filter,
  FolderOpen,
  Calendar,
  X,
  History,
  RotateCcw,
  FileSearch,
  Database,
  RefreshCw,
  Link2,
  Share2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

// Document sharing components
import ShareModal from "./ShareModal.jsx";
import ActiveShares from "./ActiveShares.jsx";

export default function DocumentsTab({ propertyId }) {
  const { getToken } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [selectedDocVersions, setSelectedDocVersions] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [contentSearchQuery, setContentSearchQuery] = useState("");
  const [backups, setBackups] = useState([]);
  const [showBackupsModal, setShowBackupsModal] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [documentsToShare, setDocumentsToShare] = useState([]);
  const [shareRefreshTrigger, setShareRefreshTrigger] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Document categories
  const categories = [
    { id: "all", label: "All Documents" },
    { id: "mortgage", label: "Mortgage & Financing" },
    { id: "insurance", label: "Insurance" },
    { id: "tax", label: "Property Tax" },
    { id: "legal", label: "Legal Documents" },
    { id: "maintenance", label: "Maintenance & Repairs" },
    { id: "warranty", label: "Warranties" },
    { id: "inspection", label: "Inspections" },
    { id: "other", label: "Other" },
  ];

  // Fetch documents
  useEffect(() => {
    fetchDocuments();
  }, [propertyId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const res = await fetch(`/api/documents/${propertyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error("Failed to fetch documents");
      }

      const data = await res.json();
      setDocuments(data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  // Upload handler
  const handleUpload = async (file, category) => {
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);
      formData.append("propertyId", propertyId);

      const token = getToken();
      console.log('Upload token:', token ? 'present' : 'missing');

      const res = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      console.log('Upload response status:', res.status);

      const responseText = await res.text();
      console.log('Upload response:', responseText.substring(0, 200));

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error(`Server returned invalid response: ${responseText.substring(0, 100)}`);
      }

      if (!res.ok) {
        throw new Error(data.error || "Upload failed");
      }

      if (data.success) {
        await fetchDocuments();
        alert(`Document uploaded successfully! ${data.ocrStatus || ""}`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(`Failed to upload document: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Delete handler
  const handleDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document and all its versions?")) {
      return;
    }

    try {
      const token = getToken();
      const res = await fetch(`/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error("Delete failed");
      }

      await fetchDocuments();
      alert("Document deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete document");
    }
  };

  const closeViewer = () => {
    if (viewingDoc?.accessUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(viewingDoc.accessUrl);
    }
    setViewerOpen(false);
    setViewingDoc(null);
  };

  // Ask the API for a short-lived URL. Local files come back as a same-origin
  // path that still requires the bearer token, so load those as blobs.
  const resolveAccessUrl = async (doc, { download = false, version } = {}) => {
    const token = getToken();
    const params = new URLSearchParams();
    if (download) params.set("download", "1");
    if (version != null) params.set("version", String(version));
    const query = params.toString();
    const res = await fetch(`/api/documents/${encodeURIComponent(doc.id)}/url${query ? `?${query}` : ""}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || "Could not open document");
    }
    if (data.delivery === "local") {
      const fileRes = await fetch(data.url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!fileRes.ok) {
        throw new Error("Could not open document");
      }
      const blob = await fileRes.blob();
      return URL.createObjectURL(blob);
    }
    return data.url;
  };

  // View document
  const viewDocument = async (doc) => {
    try {
      const accessUrl = await resolveAccessUrl(doc);
      setViewingDoc({ ...doc, accessUrl });
      setViewerOpen(true);
      setZoomLevel(1);
    } catch (error) {
      console.error("Open document error:", error);
      alert(error.message || "Could not open document");
    }
  };

  // Download document
  const downloadDocument = async (doc) => {
    try {
      const accessUrl = await resolveAccessUrl(doc, { download: true });
      const link = document.createElement("a");
      link.href = accessUrl;
      link.download = doc.name;
      if (!accessUrl.startsWith("blob:")) {
        link.target = "_blank";
        link.rel = "noopener";
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (accessUrl.startsWith("blob:")) {
        setTimeout(() => URL.revokeObjectURL(accessUrl), 1000);
      }
    } catch (error) {
      console.error("Download document error:", error);
      alert(error.message || "Could not download document");
    }
  };

  // View version history
  const viewVersionHistory = async (doc) => {
    try {
      const token = getToken();
      const res = await fetch(`/api/documents/${doc.id}/versions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to fetch versions");

      const data = await res.json();
      setSelectedDocVersions({ ...doc, ...data });
      setVersionHistoryOpen(true);
    } catch (error) {
      console.error("Error fetching versions:", error);
      alert("Failed to load version history");
    }
  };

  // Restore version
  const restoreVersion = async (documentId, versionNumber) => {
    if (!window.confirm(`Restore to version ${versionNumber}? This will create a new version.`)) {
      return;
    }

    try {
      const token = getToken();
      const res = await fetch(`/api/documents/${documentId}/restore/${versionNumber}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Restore failed");

      const data = await res.json();
      alert(data.message);
      setVersionHistoryOpen(false);
      await fetchDocuments();
    } catch (error) {
      console.error("Restore error:", error);
      alert("Failed to restore version");
    }
  };

  // Content search (OCR)
  const searchContent = async () => {
    if (!contentSearchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      const token = getToken();
      const res = await fetch(`/api/documents/search/${propertyId}?query=${encodeURIComponent(contentSearchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Search failed");

      const data = await res.json();
      setSearchResults(data);
    } catch (error) {
      console.error("Search error:", error);
      alert("Content search failed");
    }
  };

  // Create manual backup
  const createBackup = async () => {
    try {
      const token = getToken();
      const res = await fetch('/api/documents/backup/create', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Backup failed");

      const data = await res.json();
      alert(data.message);
      fetchBackups();
    } catch (error) {
      console.error("Backup error:", error);
      alert("Failed to create backup");
    }
  };

  // Fetch backups
  const fetchBackups = async () => {
    try {
      const token = getToken();
      const res = await fetch('/api/documents/backup/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to fetch backups");

      const data = await res.json();
      setBackups(data.backups);
      setShowBackupsModal(true);
    } catch (error) {
      console.error("Error fetching backups:", error);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) {
      return <ImageIcon className="w-5 h-5" />;
    } else if (fileType === "application/pdf") {
      return <FileText className="w-5 h-5" />;
    }
    return <File className="w-5 h-5" />;
  };

  // Filter documents
  const filteredDocuments = (searchResults?.results || documents).filter((doc) => {
    const matchesSearch = doc.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory =
      filterCategory === "all" || doc.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const documentsByCategory = categories.reduce((acc, cat) => {
    if (cat.id !== "all") {
      acc[cat.id] = filteredDocuments.filter((doc) => doc.category === cat.id);
    }
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Backup Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FolderOpen className="w-6 h-6 text-hn-red" />
          <h2 className="text-2xl font-semibold text-hn-dark">
            Document Vault
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchBackups}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
            title="View backups"
          >
            <Database className="w-4 h-4" />
            Backups
          </button>
          <span className="text-sm text-gray-500">
            {documents.length} document{documents.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Active Shares Section */}
      <ActiveShares propertyId={propertyId} refreshTrigger={shareRefreshTrigger} />

      {/* Content Search Bar (OCR) */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <FileSearch className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-blue-900">Search Document Content</h3>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search inside PDFs and images (OCR)..."
            value={contentSearchQuery}
            onChange={(e) => setContentSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchContent()}
            className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={searchContent}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
          >
            Search
          </button>
          {searchResults && (
            <button
              onClick={() => { setSearchResults(null); setContentSearchQuery(""); }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              Clear
            </button>
          )}
        </div>
        {searchResults && (
          <p className="text-sm text-blue-700 mt-2">
            Found {searchResults.count} document{searchResults.count !== 1 ? "s" : ""} matching "{searchResults.query}"
          </p>
        )}
      </div>

      {/* Name Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-hn-red"
          />
        </div>

        {/* Category Filter */}
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-hn-red appearance-none bg-white"
          >
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Upload Zone */}
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 bg-gray-50 hover:border-hn-red transition">
        <label className="cursor-pointer flex flex-col items-center gap-3">
          <Upload className="w-12 h-12 text-hn-red" />
          <div className="text-center">
            <p className="text-sm font-semibold text-hn-dark">
              {uploading ? "Uploading & processing with OCR..." : "Click to upload or drag and drop"}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PDF, JPG, PNG up to 10MB • Text will be automatically extracted
            </p>
          </div>
          <input
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleUpload(e.target.files[0], "other")}
            disabled={uploading}
          />
        </label>
      </div>

      {/* Documents List */}
      {filteredDocuments.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {searchQuery || filterCategory !== "all" || searchResults
              ? "No documents match your search"
              : "No documents uploaded yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(documentsByCategory).map(([categoryId, docs]) => {
            if (docs.length === 0) return null;

            const category = categories.find((c) => c.id === categoryId);

            return (
              <div key={categoryId} className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {category?.label}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {docs.map((doc) => (
                    <div
                      key={doc.id}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition group"
                    >
                      {/* File Icon & Name */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-hn-red">
                          {getFileIcon(doc.fileType)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-hn-dark truncate">
                            {doc.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(doc.size)}
                          </p>
                          {doc.version > 1 && (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full mt-1">
                              <History className="w-3 h-3" />
                              v{doc.version}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(doc.uploadedAt)}</span>
                        <span>•</span>
                        <span>{doc.uploadedBy}</span>
                      </div>

                      {/* OCR Badge */}
                      {doc.extractedText && (
                        <div className="mb-3 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                          ✓ Text extracted ({doc.extractedText.length} chars)
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => viewDocument(doc)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-xs font-medium"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        {doc.version > 1 && (
                          <button
                            onClick={() => viewVersionHistory(doc)}
                            className="flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                            title="Version History"
                          >
                            <History className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setDocumentsToShare([doc]);
                            setShareModalOpen(true);
                          }}
                          className="flex items-center justify-center px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition"
                          title="Share"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => downloadDocument(doc)}
                          className="flex items-center justify-center px-3 py-2 bg-hn-red text-white rounded-lg hover:bg-red-700 transition"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="flex items-center justify-center px-3 py-2 bg-gray-100 text-red-600 rounded-lg hover:bg-red-50 transition"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewerOpen && viewingDoc && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-hn-dark truncate">
                  {viewingDoc.name}
                </h3>
                <p className="text-xs text-gray-500">
                  {formatFileSize(viewingDoc.size)} • {formatDate(viewingDoc.uploadedAt)} • v{viewingDoc.version || 1}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => setZoomLevel((z) => Math.max(0.25, z - 0.25))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                  title="Zoom out"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 w-12 text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  onClick={() => setZoomLevel((z) => Math.min(4, z + 0.25))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                  title="Zoom in"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={closeViewer}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6 bg-gray-50">
              {viewingDoc.fileType.startsWith("image/") ? (
                <div className="flex items-start justify-center min-h-full">
                  <img
                    src={viewingDoc.accessUrl}
                    alt={viewingDoc.name}
                    style={{
                      transform: `scale(${zoomLevel})`,
                      transformOrigin: "top center",
                      transition: "transform 0.15s ease",
                    }}
                    className="max-w-full h-auto"
                  />
                </div>
              ) : viewingDoc.fileType === "application/pdf" ? (
                <div
                  className="w-full"
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: "top center",
                    transition: "transform 0.15s ease",
                    height: `${600 * zoomLevel}px`,
                  }}
                >
                  <iframe
                    src={viewingDoc.accessUrl}
                    className="w-full border-0"
                    style={{ height: "600px" }}
                    title={viewingDoc.name}
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <File className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Preview not available</p>
                  <button
                    onClick={() => downloadDocument(viewingDoc)}
                    className="mt-4 px-4 py-2 bg-hn-red text-white rounded-lg hover:bg-red-700 transition"
                  >
                    Download to View
                  </button>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => downloadDocument(viewingDoc)}
                className="flex items-center gap-2 px-4 py-2 bg-hn-red text-white rounded-lg hover:bg-red-700 transition"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={closeViewer}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {versionHistoryOpen && selectedDocVersions && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-hn-dark">Version History</h3>
                <p className="text-sm text-gray-500">{selectedDocVersions.name}</p>
              </div>
              <button
                onClick={() => setVersionHistoryOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6 space-y-3">
              {/* Current Version */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-blue-900">
                        Version {selectedDocVersions.currentVersion} (Current)
                      </span>
                      {selectedDocVersions.restoredFrom && (
                        <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                          Restored from v{selectedDocVersions.restoredFrom}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-blue-700 mt-1">
                      {formatDate(selectedDocVersions.uploadedAt)} • {selectedDocVersions.uploadedBy}
                    </p>
                  </div>
                  <button
                    onClick={() => downloadDocument(selectedDocVersions)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                  >
                    Download
                  </button>
                </div>
              </div>

              {/* Previous Versions */}
              {selectedDocVersions.versions.map((version) => (
                <div key={version.version} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-gray-900">
                        Version {version.version}
                      </span>
                      <p className="text-sm text-gray-600 mt-1">
                        {formatDate(version.uploadedAt)} • {version.uploadedBy}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => restoreVersion(selectedDocVersions.id, version.version)}
                        className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition text-sm"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Backups Modal */}
      {showBackupsModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-hn-red" />
                <h3 className="text-lg font-semibold text-hn-dark">Backup Management</h3>
              </div>
              <button
                onClick={() => setShowBackupsModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <button
                onClick={createBackup}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-hn-red text-white rounded-lg hover:bg-red-700 transition font-medium"
              >
                <RefreshCw className="w-5 h-5" />
                Create Backup Now
              </button>

              <div className="space-y-2">
                <h4 className="font-semibold text-gray-700">Available Backups ({backups.length})</h4>
                {backups.length === 0 ? (
                  <p className="text-gray-500 text-sm">No backups available</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {backups.map((backup) => (
                      <div key={backup.name} className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{backup.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatDate(backup.created)} • {formatFileSize(backup.size)}
                          </p>
                        </div>
                        <a
                          href={`/api/documents/backup/download/${backup.name}`}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-semibold mb-1">Automatic Backups Enabled:</p>
                <p>• Daily: 2:00 AM</p>
                <p>• Weekly: Sunday 12:00 PM</p>
                <p className="mt-2 text-xs">Last 10 backups are automatically retained</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setDocumentsToShare([]);
        }}
        documents={documentsToShare}
        propertyId={propertyId}
        onShareCreated={() => setShareRefreshTrigger((t) => t + 1)}
      />
    </div>
  );
}
